import React, { useState, useCallback } from 'react';
import { Link as LinkIcon, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useCreateFile } from '../../hooks/useDigitalFiles';
import { toast } from 'sonner';
import { FILE_TYPE_CONFIG, inferProvider } from './fileConstants';

/**
 * FileLinkDialog
 * Tracks a file by external URL (Google Drive, Dropbox, OneDrive, any link).
 * No uploads — we never host file content.
 */
export default function FileLinkDialog({ open, onClose, filmId }) {
  const createMutation = useCreateFile();

  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [fileType, setFileType] = useState('other');
  const [submitting, setSubmitting] = useState(false);

  const reset = useCallback(() => {
    setUrl('');
    setName('');
    setDescription('');
    setFileType('other');
    setSubmitting(false);
  }, []);

  const handleClose = useCallback(() => {
    if (!submitting) {
      reset();
      onClose();
    }
  }, [submitting, reset, onClose]);

  // Auto-suggest a sensible name from the URL the first time it parses.
  const handleUrlChange = useCallback((rawUrl) => {
    setUrl(rawUrl);
    if (name) return;
    try {
      const u = new URL(rawUrl);
      const last = u.pathname.split('/').filter(Boolean).pop();
      if (last && last.length < 80) {
        const decoded = decodeURIComponent(last).replace(/[-_+]/g, ' ').trim();
        if (decoded) setName(decoded);
      }
    } catch {
      /* ignore */
    }
  }, [name]);

  const provider = url ? inferProvider(url) : null;

  const isValidUrl = (() => {
    if (!url) return false;
    try {
      const u = new URL(url);
      return u.protocol === 'http:' || u.protocol === 'https:' || u.protocol === 'ftp:';
    } catch {
      return false;
    }
  })();

  const handleSubmit = async () => {
    if (!filmId) {
      toast.error('Missing film context');
      return;
    }
    if (!isValidUrl) {
      toast.error('Please enter a valid URL (https://...)');
      return;
    }
    if (!name.trim()) {
      toast.error('Please give this file a name');
      return;
    }

    setSubmitting(true);
    try {
      await createMutation.mutateAsync({
        film_id: filmId,
        name: name.trim(),
        external_url: url.trim(),
        description: description.trim() || undefined,
        file_type: fileType,
      });
      toast.success('File link added');
      reset();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Failed to add: ${detail}`);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="file-link-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Add a File Link</DialogTitle>
          <DialogDescription>
            Paste a link from Google Drive, Dropbox, OneDrive, Frame.io, or any URL.
            FrameFlow tracks the metadata — your file stays where it lives.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="file-url">File URL <span className="text-destructive">*</span></Label>
            <div className="relative">
              <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="file-url"
                data-testid="file-link-url"
                type="url"
                placeholder="https://drive.google.com/file/d/..."
                value={url}
                onChange={(e) => handleUrlChange(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
            {provider?.host && (
              <p className="text-[11px] text-muted-foreground">
                Detected: <span className="font-medium text-foreground">{provider.label}</span>
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="file-name">Display Name <span className="text-destructive">*</span></Label>
            <Input
              id="file-name"
              data-testid="file-link-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Scene 4 Storyboards"
            />
          </div>

          <div className="space-y-1.5">
            <Label>File Type</Label>
            <Select value={fileType} onValueChange={setFileType}>
              <SelectTrigger data-testid="file-link-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(FILE_TYPE_CONFIG).map(([val, cfg]) => (
                  <SelectItem key={val} value={val}>
                    <span className="flex items-center gap-2"><span>{cfg.icon}</span> {cfg.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="file-desc">Description (optional)</Label>
            <Textarea
              id="file-desc"
              data-testid="file-link-description"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Notes about this file..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={submitting}
            data-testid="file-link-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !isValidUrl || !name.trim()}
            className="gap-2"
            data-testid="file-link-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <LinkIcon className="h-4 w-4" />}
            {submitting ? 'Saving...' : 'Add Link'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
